// useTableInfiniteScroll.ts
import type { Row, Table } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";

type UseTableInfiniteScrollOptions = {
  getScrollElement?: () => Element | null;
};

export function useTableInfiniteScroll<TData>(
  table: Table<TData>,
  pageSize = 10,
  options?: UseTableInfiniteScrollOptions,
) {
  const rows: Row<TData>[] = table.getRowModel().rows;

  const [itemsToRender, setItemsToRender] = useState(() =>
    Math.min(pageSize, rows.length || pageSize),
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const totalRows = rows.length;
  const hasMoreRows = itemsToRender < totalRows;

  const visibleRows = useMemo(
    () => rows.slice(0, itemsToRender),
    [rows, itemsToRender],
  );

  // Clamp when table rows change (e.g., filters/search/sort reduce results)
  useEffect(() => {
    setItemsToRender((current) => {
      if (totalRows === 0) return pageSize;
      const base = current || pageSize;
      return Math.min(Math.max(base, pageSize), totalRows);
    });
  }, [totalRows, pageSize]);

  // Grow window when sentinel comes into view
  const getScrollElement = options?.getScrollElement;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMoreRows) return;

    const rootEl = getScrollElement?.() ?? null;

    // Fallback if IO is unavailable (older browsers/SSR)
    if (typeof IntersectionObserver === "undefined") {
      setItemsToRender((c) => Math.min(c + pageSize, totalRows));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setItemsToRender((c) => Math.min(c + pageSize, totalRows));
        }
      },
      { root: rootEl },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [hasMoreRows, pageSize, totalRows, getScrollElement]);

  /** Reset back to the first "page" (use when search/filter/sort changes) */
  const reset = () => {
    setItemsToRender(Math.min(pageSize, totalRows));
  };

  return {
    visibleRows, // Row<TData>[]
    totalRows, // number
    hasMoreRows, // boolean
    itemsToRender, // number
    setItemsToRender, // updater
    sentinelRef, // attach to your <div ref={...} />
    reset, // call when inputs change
  };
}
